/**
 * Formats a wearable data source for display, per Garmin's API Brand
 * Guidelines (issue #169): Garmin-sourced data must show "Garmin [device
 * model]" (or just "Garmin" when the model isn't known), with correct
 * capitalization — never the raw uppercase resource string.
 *
 * Other providers (Oura, Whoop, etc.) have no equivalent attribution
 * requirement from us, so they're just title-cased for consistency.
 */
export function isGarminProvider(provider?: string | null): boolean {
  return provider?.toUpperCase() === "GARMIN";
}

/**
 * Garmin's device names already start with the brand ("Garmin Forerunner
 * 245"), so the prefix is dropped wherever "Garmin" is shown separately.
 */
export function garminDeviceModel(deviceModel?: string | null): string | null {
  const model = (deviceModel ?? "").trim().replace(/^garmin\s*/i, "");
  return model || null;
}

export function formatProviderAttribution(
  provider?: string | null,
  deviceModel?: string | null,
): string | null {
  if (!provider) return null;

  if (isGarminProvider(provider)) {
    const model = garminDeviceModel(deviceModel);
    return model ? `Garmin ${model}` : "Garmin";
  }

  const lower = provider.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

/**
 * Sentence-style attribution for aggregate/derived/combined views (trend
 * charts, insights, women's health data) where a single "Garmin [device
 * model]" badge doesn't fit a specific data point — per Garmin's "Combined
 * or Derived Data" brand guideline. Pass whether the user has an active
 * Garmin device connected; exact per-point precision isn't required by the
 * guideline, just that Garmin is listed as a contributing source whenever
 * it plausibly is one.
 */
export function derivedDataAttribution(hasGarminDevice: boolean): string {
  return hasGarminDevice
    ? "Insights derived in part from Garmin device-sourced data."
    : "Imported from Fitness Wearable";
}
